import BracketSportHeading from "@/components/bracket/BracketSportHeading"
import BracketTree from "@/components/bracket/BracketTree"
import EmptyMessage from "@/components/common/EmptyMessage"
import type { BracketDetail } from "@/domain/bracket/model/Bracket"
import { groupBySport } from "@/domain/bracket/utils/BracketUtils"

interface BracketAllTreesProps {
  brackets: BracketDetail[]
}

/**
 * 대회 전체 대진표 (모든 체급의 트리를 한 화면에)
 *
 * 종이 대진표를 펼쳐 놓은 것과 같다 — **종목별로 묶고** 종목 안에서 체급 오름차순으로 위에서 아래로 늘어놓는다.
 * 경기 번호가 종목마다 1부터라 종목 제목으로 갈라 두어야 어느 종목의 「N경기」인지 읽힌다.
 * 체급마다 규모(8강 · 4강 · 결승)와 부 · 성별을 머리에 달아 어느 표인지 바로 읽히게 한다.
 *
 * 체급 하나만 크게 볼 때는 「체급별 대진표」 탭을 쓴다 — 여기서는 훑어보는 것이 목적이다.
 */
export default function BracketAllTrees({
  brackets,
}: Readonly<BracketAllTreesProps>) {
  if (brackets.length === 0) {
    return <EmptyMessage message="아직 편성된 대진표가 없습니다." />
  }

  const sportGroups = groupBySport(brackets)

  return (
    <div className="flex flex-col gap-14">
      {sportGroups.map((sportGroup) => (
        <section
          key={sportGroup.sportLabel}
          aria-label={`${sportGroup.sportLabel} 대진표`}
        >
          <BracketSportHeading
            sportLabel={sportGroup.sportLabel}
            description={`체급 ${sportGroup.items.length}개`}
            level="h3"
          />

          <div className="mt-6 flex flex-col gap-10">
            {sportGroup.items.map((bracket) => (
              <section
                key={bracket.id}
                aria-label={`${bracket.weightClassName} 대진표`}
              >
                <h4 className="app-txt-h3">
                  {bracket.weightClassName} {bracket.sizeLabel}
                </h4>
                <p className="app-txt-body2 mt-1.5 text-app-text-200">
                  {bracket.divisionLabel} · {bracket.genderLabel} · 선수{" "}
                  {bracket.slotCount}자리
                </p>

                <div className="mt-4 overflow-x-auto pb-4">
                  <BracketTree rounds={bracket.rounds} />
                </div>
              </section>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
